import { SubCategoryTabs } from './SubCategoryTabs';
import { usePredictionMarketCategories } from '@/lib/endpoints';
import { ALL_SUB_CATEGORY_ID, useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useHiddenCategories } from '@/lib/territory/useTerritory';
import { cn } from '@/lib/utils';

export interface SubCategoryBarProps {
  className?: string;
  /**
   * When provided, only sub-categories whose code is in this set render —
   * the Live page passes the codes that actually have eligible events, so
   * empty chips hide. Omitted (the lobby) ⇒ all sub-categories render.
   */
  allowedCodes?: ReadonlySet<string>;
}

/**
 * Sub-category chip row for the category feed, rendered in the page body
 * between {@link CategoryBanner} and the feed so the chips sit directly on
 * top of the list (Figma 6409-160416 / 6492-185212). Not sticky — it
 * scrolls away with the banner; the feed's "Back to Top" affordance covers
 * returning to the filters.
 *
 * Self-nulls on Trending (no `?category=`), for territory-blocked
 * categories, and for categories without sub-categories — mirroring
 * {@link CategoryBanner}. Shares the categories query cache, so it costs
 * no extra network request.
 */
export function SubCategoryBar({ className, allowedCodes }: SubCategoryBarProps) {
  const { category, subCategory, setSubCategory } = useAppSearchParams();
  const { data } = usePredictionMarketCategories();
  const hiddenCategories = useHiddenCategories();

  if (!category) return null;
  if (hiddenCategories.includes(category.toUpperCase())) return null;

  const all = data?.find((cat) => cat.code === category)?.subCategories ?? [];
  const subCategories = allowedCodes ? all.filter((s) => allowedCodes.has(s.code)) : all;
  if (subCategories.length === 0) return null;

  return (
    // Width cap tracks the category feed below it (760px → 1300px at xl, cf. BinaryLobbyFeed).
    <div className={cn('w-full max-w-[760px] xl:max-w-[1300px]', className)}>
      <SubCategoryTabs
        subCategories={subCategories}
        activeSubCategory={subCategory ?? ALL_SUB_CATEGORY_ID}
        onSubCategoryChange={setSubCategory}
      />
    </div>
  );
}
